import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { getIntegrationsWithId, updateIntegrationWithId } from '../utils/api/integrationAPI';
import { toast } from 'sonner';

const EditOrganisationLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        api_key: '',
        type: '',
        status: true
    });

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        getIntegrationsWithId(id)
            .then(data => {
                const org = data.integration;


                setFormData({
                    name: org.name || '',
                    api_key: org.api_key || '',
                    type: org.type || '',
                    status: org.status ?? true
                });
            })
            .catch(err => console.error('Failed to load organisation:', err))
            .finally(() => setIsLoading(false));
    }, [id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const res = await updateIntegrationWithId(id, formData);

            toast.success(res?.message || 'Integration updated successfully!');
            navigate(-1);
        } catch (err) {
            console.error('Update failed:', err);
            toast.error(err?.message || 'Failed to update integration.');
        } finally {
            setIsLoading(false);
        }
    };

    // Styling constants
    const cardStyle = {
        borderRadius: '12px',
        boxShadow: '0 4px 18px rgba(0, 0, 0, 0.08)',
        border: 'none',
        backgroundColor: '#fff'
    };

    const formLabelStyle = {
        fontSize: '14px',
        fontWeight: '600',
        color: '#444',
        marginBottom: '8px',
        display: 'block'
    };

    const inputStyle = {
        padding: '10px 16px',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        fontSize: '14px',
        width: '100%',
        backgroundColor: '#f9fafb'
    };

    return (
        <div className="row gy-4">
            <div className="card h-100" style={cardStyle}>
                <div className="card-body p-24" style={{ padding: '32px' }}>
                    <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '24px' }}>
                        <h2 style={{ fontSize: '22px', fontWeight: '600', color: '#333', margin: '0 0 8px 0' }}>
                            Edit Integration
                        </h2>
                        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                            Update Integration details and preferences
                        </p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {isLoading && (
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#e8f4ff',
                                borderRadius: '8px',
                                textAlign: 'center',
                                color: '#0d6efd',
                                marginBottom: '20px'
                            }}>
                                <p style={{ margin: 0 }}>Loading data...</p>
                            </div>
                        )}

                        <div className="row">
                            {/* Name */}
                            <div className="col-sm-6 mb-20">
                                <label style={formLabelStyle}>
                                    Name <span style={{ color: '#dc3545' }}>*</span>
                                </label>
                                <input
                                    name="name"
                                    type="text"
                                    style={inputStyle}
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Integration Name"
                                    required
                                />
                            </div>

                            {/* Status */}
                            <div className="col-sm-6 mb-20">
                                <label style={formLabelStyle}>Status</label>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    backgroundColor: '#f9fafb',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0'
                                }}>
                                    <span style={{
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: formData.status ? '#0d6efd' : '#6c757d'
                                    }}>
                                        {formData.status ? 'Active' : 'Inactive'}
                                    </span>
                                    <div style={{ flexGrow: 1 }}></div>
                                    <label style={{
                                        position: 'relative',
                                        display: 'inline-block',
                                        width: '46px',
                                        height: '24px',
                                        margin: 0
                                    }}>
                                        <input
                                            name="status"
                                            type="checkbox"
                                            checked={formData.status}
                                            onChange={handleChange}
                                            style={{
                                                opacity: '0',
                                                width: '0',
                                                height: '0'
                                            }}
                                        />
                                        <span style={{
                                            position: 'absolute',
                                            cursor: 'pointer',
                                            top: '0',
                                            left: '0',
                                            right: '0',
                                            bottom: '0',
                                            backgroundColor: formData.status ? '#0d6efd' : '#e2e2e2',
                                            transition: 'background-color 0.3s ease',
                                            borderRadius: '34px',
                                            border: `1px solid ${formData.status ? '#0d6efd' : '#ccc'}`
                                        }}></span>
                                        <span style={{
                                            position: 'absolute',
                                            height: '18px',
                                            width: '18px',
                                            left: formData.status ? '24px' : '4px',
                                            bottom: '3px',
                                            backgroundColor: 'white',
                                            transition: '0.3s',
                                            borderRadius: '50%',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}></span>
                                    </label>
                                </div>
                            </div>

                            {/* API KEY */}
                            <div className="col-sm-6 mb-20">
                                <label style={formLabelStyle}>
                                    API KEY <span style={{ color: '#dc3545' }}>*</span>
                                </label>
                                <input
                                    name="api_key"
                                    type="text"
                                    style={inputStyle}
                                    value={formData.api_key}
                                    onChange={handleChange}
                                    placeholder="Enter the API Key"
                                    required
                                />
                            </div>

                            {/* Type */}
                            <div className="col-sm-6 mb-20">
                                <label style={formLabelStyle}>
                                    Type <span style={{ color: '#dc3545' }}>*</span>
                                </label>
                                <input
                                    name="type"
                                    type="text"
                                    style={inputStyle}
                                    value={formData.type}
                                    onChange={handleChange}
                                    placeholder="Enter the Integration Type"
                                    required
                                />
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '16px',
                            marginTop: '32px',
                            padding: '16px 0 0',
                            borderTop: '1px solid #f0f0f0'
                        }}>
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                style={{
                                    padding: '10px 32px',
                                    borderRadius: '8px',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    backgroundColor: 'transparent',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#fff1f1'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    padding: '10px 32px',
                                    borderRadius: '8px',
                                    border: '1px solid #0d6efd',
                                    color: '#fff',
                                    backgroundColor: '#0d6efd',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    opacity: isLoading ? 0.7 : 1,
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={e => !isLoading && (e.currentTarget.style.backgroundColor = '#0b5ed7')}
                                onMouseOut={e => !isLoading && (e.currentTarget.style.backgroundColor = '#0d6efd')}
                            >
                                {isLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditOrganisationLayer;
